import type { VehicleBodyType, VehicleCondition, VehicleFuel, VehicleTransmission } from '@/lib/sanity/dto';
import {
    VEHICLE_BODY_TYPE_LABELS,
    VEHICLE_CONDITION_FILTER_LABELS,
    VEHICLE_FUEL_LABELS,
    VEHICLE_TRANSMISSION_LABELS,
    type VehicleCardModel,
} from '@/features/vehicle';
import { matchesFilters, modelKey, type VehicleFilters } from './vehicleFilters';

export type VehicleFacetOption<T extends string = string> = {
    value: T;
    label: string;
    count: number;
};

export type VehicleModelGroup = {
    make: string;
    options: VehicleFacetOption[];
};

export type VehicleFacetOptions = {
    condition: VehicleFacetOption<VehicleCondition>[];
    make: VehicleFacetOption[];
    modelGroups: VehicleModelGroup[];
    bodyType: VehicleFacetOption<VehicleBodyType>[];
    fuel: VehicleFacetOption<VehicleFuel>[];
    transmission: VehicleFacetOption<VehicleTransmission>[];
};

function countBy<T>(vehicles: VehicleCardModel[], pick: (vehicle: VehicleCardModel) => T | undefined): Map<T, number> {
    const counts = new Map<T, number>();

    for (const vehicle of vehicles) {
        const value = pick(vehicle);
        if (value !== undefined) counts.set(value, (counts.get(value) ?? 0) + 1);
    }

    return counts;
}

/** The values the stock holds, in the fixed order of the label map. */
function fixedOrder<T extends string>(counts: Map<T, number>, labels: Record<T, string>): VehicleFacetOption<T>[] {
    return (Object.keys(labels) as T[])
        .filter((value) => counts.has(value))
        .map((value) => ({ value, label: labels[value], count: counts.get(value) ?? 0 }));
}

/** Most frequent first, ties alphabetically — the design's order for body types. */
function byFrequency<T extends string>(counts: Map<T, number>, label: (value: T) => string): VehicleFacetOption<T>[] {
    return [...counts.entries()]
        .map(([value, count]) => ({ value, label: label(value), count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'de'));
}

/** Only models with a hit under the other filters, grouped by the selected (or every) make. */
function modelGroups(
    vehicles: VehicleCardModel[],
    makes: VehicleFacetOption[],
    selectedMakes: string[],
): VehicleModelGroup[] {
    return makes
        .filter(({ value }) => !selectedMakes.length || selectedMakes.includes(value))
        .map(({ value: make }) => {
            const counts = countBy(
                vehicles.filter((vehicle) => vehicle.facets.make === make),
                (vehicle) => vehicle.facets.model,
            );

            const options = [...counts.entries()]
                .sort(([a], [b]) => a.localeCompare(b, 'de', { numeric: true }))
                .map(([model, count]) => ({ value: modelKey(make, model), label: model, count }));

            return { make, options };
        })
        .filter((group) => group.options.length > 0);
}

/**
 * The options each filter offers. Make and model count live against every other
 * active filter; the chip groups list what the stock holds.
 */
export function facetOptions(vehicles: VehicleCardModel[], filters: VehicleFilters): VehicleFacetOptions {
    const makes = byFrequency(
        countBy(vehicles, (vehicle) => vehicle.facets.make),
        (make) => make,
    );
    const makeCounts = countBy(
        vehicles.filter((vehicle) => matchesFilters(vehicle, filters, 'make')),
        (vehicle) => vehicle.facets.make,
    );

    return {
        condition: fixedOrder(
            countBy(vehicles, (vehicle) => vehicle.facets.condition),
            VEHICLE_CONDITION_FILTER_LABELS,
        ),
        make: makes.map((option) => ({ ...option, count: makeCounts.get(option.value) ?? 0 })),
        modelGroups: modelGroups(
            vehicles.filter((vehicle) => matchesFilters(vehicle, filters, 'model')),
            makes,
            filters.make,
        ),
        bodyType: byFrequency(
            countBy(vehicles, (vehicle) => vehicle.facets.bodyType),
            (bodyType) => VEHICLE_BODY_TYPE_LABELS[bodyType],
        ),
        fuel: fixedOrder(
            countBy(vehicles, (vehicle) => vehicle.facets.fuel),
            VEHICLE_FUEL_LABELS,
        ),
        transmission: fixedOrder(
            countBy(vehicles, (vehicle) => vehicle.facets.transmission),
            VEHICLE_TRANSMISSION_LABELS,
        ),
    };
}
